import { toast } from 'sonner';

// Doing one thing to many rows of an ordinary list — delete them, or patch the
// same field on all of them. Written once so that four screens cannot each
// grow their own loop, and so that a run which half works says so: a loop that
// stops at the first refusal and reports success is how a list ends up in a
// state nobody can explain.
//
// Rows are sent one at a time rather than in parallel. The lists this serves
// are small, the order is the office's own, and a burst of hundreds of writes
// at once is how a cheap server starts refusing them.

const describe = (e) => e?.response?.data?.detail || e?.message || 'refused';

/**
 * Delete each id. Returns {done, failed} and tells the office which it was,
 * naming the first reason when some failed.
 */
export async function removeMany(api, ids, noun = 'record') {
  let done = 0;
  const failures = [];
  for (const id of ids || []) {
    try {
      await api.remove(id);
      done += 1;
    } catch (e) {
      failures.push(`${id}: ${describe(e)}`);
    }
  }
  if (failures.length && done) {
    toast.error(`Deleted ${done} ${noun}(s); ${failures.length} refused — ${failures[0]}`);
  } else if (failures.length) {
    toast.error(`Could not delete those ${noun}s — ${failures[0]}`);
  } else {
    toast.success(`Deleted ${done} ${noun}(s)`);
  }
  return { done, failed: failures.length };
}

/**
 * Merge `patch` into each row. The whole row is sent back, because these
 * endpoints replace the record rather than merging into it — sending the patch
 * alone empties every other field.
 */
export async function patchMany(api, rows, patch, what = 'updated') {
  let done = 0;
  const failures = [];
  for (const row of rows || []) {
    try {
      await api.update(row.id, { ...row, ...patch });
      done += 1;
    } catch (e) {
      failures.push(`${row.id}: ${describe(e)}`);
    }
  }
  if (failures.length && done) {
    toast.error(`${done} ${what}; ${failures.length} refused — ${failures[0]}`);
  } else if (failures.length) {
    toast.error(`Could not update those — ${failures[0]}`);
  } else {
    toast.success(`${done} ${what}`);
  }
  return { done, failed: failures.length };
}

/** A confirm that names the count, for the actions nobody can undo. */
export function confirmCount(action, count, noun, extra = '') {
  return window.confirm(
    `${action} ${count} ${noun}${count === 1 ? '' : 's'}?${extra ? ` ${extra}` : ''}`);
}
